import React, { useContext, useEffect, useState } from "react";

import { NotificationsContext } from "@ui-components/Notifications";
import useLevels from "@services/hooks/useLevels";

// Ui
import { PageHeading } from "@ui-components/Container";
import Placeholder from "@ui-components/Placeholder";
import Toggle from "@ui-components/Toggle";

// Component
import { GamesLevelFilter, GamesPeriodFilter, GamesProvinceFilter } from "@pages/Games/Games.lib";
import { getListItems } from "@pages/Nominations/Observers/Observers.lib";
import RefManList from "@components/RefMan/RefManList/RefManList";
import { MemberSearch } from "@pages/Members/Members.lib";
import { getDocumentsData, PaginatedGamesWithDocuments, uploadDocument, downloadDocument, deleteDocument, markDocumentAtOffice, reportDocumentAnomaly, validateDocument } from "./Documents.lib";
import { UploadDocumentModal } from "./UploadDocumentModal";
import { DeleteDocumentModal } from "./DeleteDocumentModal";
import { MarkAtOfficeModal } from "./MarkAtOfficeModal";
import { ReportAnomalyModal } from "./ReportAnomalyModal";
import { ValidateDocumentModal } from "./ValidateDocumentModal";

// Type
import { SelectedLevel, SelectedProvince } from "../Games/Games.type";
import { RefManListItemType } from "@components/RefMan/RefManList/RefManList.type";
import { useIntl } from "react-intl";
import AuthenticationService from "@services/AuthenticationService";

let searchSelectTimeout: NodeJS.Timeout;

export function Documents() {
  const { push } = useContext(NotificationsContext);
  const levels = useLevels({ asObj: false, onlyManaged: true, onlyWithTax: true, managedGroup: "ref" });
  const intl = useIntl();
  const scopes = AuthenticationService.getUserScopes();
  const isMember = scopes.includes("absences:personal");
  const userData = AuthenticationService.getUserData();
  const currentUserId = userData?.id;
  const canEditAny = !isMember && scopes.includes("documents:upload");

  let initialStartDate = new Date();
  initialStartDate.setDate(initialStartDate.getDate() - 5);
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [filterMember, setFilterMember] = useState<string>();
  const [filterGameNumber, setFilterGameNumber] = useState<string>();
  const [selectedLevels, setSelectedLevels] = useState<SelectedLevel[]>([]);
  const [selectedProvinces, setSelectedProvinces] = useState<SelectedProvince[]>([]);
  const [data, setData] = useState<PaginatedGamesWithDocuments | undefined>(undefined);
  const [offset, setOffset] = useState<number>(0);
  const limit = 5;
  const [itemsList, setItemsList] = useState<RefManListItemType[]>();
  const [showMissing, setShowMissing] = useState<boolean>(true);
  const [showUploaded, setShowUploaded] = useState<boolean>(true);
  const [includeSecondRef, setIncludeSecondRef] = useState<boolean>(false);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [selectedGameForUpload, setSelectedGameForUpload] = useState<string | null>(null);
  const [uploadingGame, setUploadingGame] = useState<string | null>(null);
  const [deletingGame, setDeletingGame] = useState<string | null>(null);
  const [markingAtOfficeGame, setMarkingAtOfficeGame] = useState<string | null>(null);
  const [isReplacingDocument, setIsReplacingDocument] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedGameForDelete, setSelectedGameForDelete] = useState<string | null>(null);
  const [markAtOfficeModalOpen, setMarkAtOfficeModalOpen] = useState<boolean>(false);
  const [selectedGameForMarkAtOffice, setSelectedGameForMarkAtOffice] = useState<string | null>(null);
  const [reportAnomalyModalOpen, setReportAnomalyModalOpen] = useState<boolean>(false);
  const [selectedGameForReportAnomaly, setSelectedGameForReportAnomaly] = useState<string | null>(null);
  const [reportingAnomalyGame, setReportingAnomalyGame] = useState<string | null>(null);
  const [validateDocumentModalOpen, setValidateDocumentModalOpen] = useState<boolean>(false);
  const [selectedGameForValidate, setSelectedGameForValidate] = useState<string | null>(null);
  const [validatingDocumentGame, setValidatingDocumentGame] = useState<string | null>(null);
  const [selectedGamesForDownload, setSelectedGamesForDownload] = useState<Set<string>>(new Set());
  const [isDownloadingBatch, setIsDownloadingBatch] = useState<boolean>(false);

  const [textFilters, setTextFilters] = useState({ game_number: "", city: "", team: "", member: "" });
  useEffect(() => {
    setTextFilters({
      game_number: filterGameNumber && filterGameNumber.length >= 2 ? filterGameNumber : "",
      city: "",
      team: "",
      member: filterMember && filterMember.length > 2 ? filterMember : ""
    });
  }, [filterMember, filterGameNumber]);

  useEffect(() => {
    setData(undefined);
    void getDocumentsData(
      startDate,
      endDate,
      textFilters,
      selectedProvinces,
      selectedLevels,
      showMissing,
      showUploaded,
      includeSecondRef,
      push,
      limit,
      offset,
      setData
    );
  }, [limit, offset, startDate, endDate, selectedLevels, selectedProvinces, showMissing, showUploaded, includeSecondRef]);

  /**
   * Handler for
   * - textFilters
   */
  useEffect(() => {
    if (searchSelectTimeout) {
      clearTimeout(searchSelectTimeout);
    }
    searchSelectTimeout = setTimeout(() => {
      void getDocumentsData(startDate, endDate, textFilters, selectedProvinces, selectedLevels, showMissing, showUploaded, includeSecondRef, push, limit, offset, setData);
    }, 600);
  }, [textFilters]);

  const handleDownload = async (codGame: string) => {
    await downloadDocument(codGame, push);
  };

  const handleUploadClick = (codGame: string, isReplacing: boolean = false) => {
    setSelectedGameForUpload(codGame);
    setIsReplacingDocument(isReplacing);
    setUploadModalOpen(true);
  };

  const handleUploadFiles = async (files: File[]) => {
    if (!selectedGameForUpload) return;
    
    setUploadingGame(selectedGameForUpload);
    try {
      await uploadDocument(selectedGameForUpload, files, push);
      // Close modal and refresh data after successful upload
      setUploadModalOpen(false);
      void getDocumentsData(
        startDate,
        endDate,
        textFilters,
        selectedProvinces,
        selectedLevels,
        showMissing,
        showUploaded,
        includeSecondRef,
        push,
        limit,
        offset,
        setData
      );
    } catch (error) {
      // Error already handled in uploadDocument
    } finally {
      setUploadingGame(null);
    }
  };

  const handleCloseModal = () => {
    if (!uploadingGame) {
      setUploadModalOpen(false);
      setSelectedGameForUpload(null);
      setIsReplacingDocument(false);
    }
  };

  const handleDeleteClick = (codGame: string) => {
    setSelectedGameForDelete(codGame);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedGameForDelete) return;
    
    setDeletingGame(selectedGameForDelete);
    try {
      await deleteDocument(selectedGameForDelete, push);
      // Close modal and refresh data after successful deletion
      setDeleteModalOpen(false);
      void getDocumentsData(
        startDate,
        endDate,
        textFilters,
        selectedProvinces,
        selectedLevels,
        showMissing,
        showUploaded,
        includeSecondRef,
        push,
        limit,
        offset,
        setData
      );
    } catch (error) {
      // Error already handled in deleteDocument
    } finally {
      setDeletingGame(null);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!deletingGame) {
      setDeleteModalOpen(false);
      setSelectedGameForDelete(null);
    }
  };

  const handleMarkAtOfficeClick = (codGame: string) => {
    setSelectedGameForMarkAtOffice(codGame);
    setMarkAtOfficeModalOpen(true);
  };

  const handleMarkAtOfficeConfirm = async () => {
    if (!selectedGameForMarkAtOffice) return;
    
    setMarkingAtOfficeGame(selectedGameForMarkAtOffice);
    try {
      await markDocumentAtOffice(selectedGameForMarkAtOffice, push);
      // Close modal and refresh data after successful marking
      setMarkAtOfficeModalOpen(false);
      void getDocumentsData(
        startDate,
        endDate,
        textFilters,
        selectedProvinces,
        selectedLevels,
        showMissing,
        showUploaded,
        includeSecondRef,
        push,
        limit,
        offset,
        setData
      );
    } catch (error) {
      // Error already handled in markDocumentAtOffice
    } finally {
      setMarkingAtOfficeGame(null);
    }
  };

  const handleCloseMarkAtOfficeModal = () => {
    if (!markingAtOfficeGame) {
      setMarkAtOfficeModalOpen(false);
      setSelectedGameForMarkAtOffice(null);
    }
  };

  const handleReportAnomalyClick = (codGame: string) => {
    setSelectedGameForReportAnomaly(codGame);
    setReportAnomalyModalOpen(true);
  };

  const handleReportAnomalyConfirm = async (desAnomaly: string) => {
    if (!selectedGameForReportAnomaly) return;
    
    setReportingAnomalyGame(selectedGameForReportAnomaly);
    try {
      await reportDocumentAnomaly(selectedGameForReportAnomaly, desAnomaly, push);
      // Close modal and refresh data after successful reporting
      setReportAnomalyModalOpen(false);
      void getDocumentsData(
        startDate,
        endDate,
        textFilters,
        selectedProvinces,
        selectedLevels,
        showMissing,
        showUploaded,
        includeSecondRef,
        push,
        limit,
        offset,
        setData
      );
    } catch (error) {
      // Error already handled in reportDocumentAnomaly
    } finally {
      setReportingAnomalyGame(null);
    }
  };

  const handleCloseReportAnomalyModal = () => {
    if (!reportingAnomalyGame) {
      setReportAnomalyModalOpen(false);
      setSelectedGameForReportAnomaly(null);
    }
  };

  const handleValidateDocumentClick = (codGame: string) => {
    setSelectedGameForValidate(codGame);
    setValidateDocumentModalOpen(true);
  };

  const handleValidateDocumentConfirm = async () => {
    if (!selectedGameForValidate) return;
    
    setValidatingDocumentGame(selectedGameForValidate);
    try {
      await validateDocument(selectedGameForValidate, push);
      // Close modal and refresh data after successful validation
      setValidateDocumentModalOpen(false);
      void getDocumentsData(
        startDate,
        endDate,
        textFilters,
        selectedProvinces,
        selectedLevels,
        showMissing,
        showUploaded,
        includeSecondRef,
        push,
        limit,
        offset,
        setData
      );
    } catch (error) {
      // Error already handled in validateDocument
    } finally {
      setValidatingDocumentGame(null);
    }
  };

  const handleCloseValidateDocumentModal = () => {
    if (!validatingDocumentGame) {
      setValidateDocumentModalOpen(false);
      setSelectedGameForValidate(null);
    }
  };

  const handleToggleSelection = (codGame: string) => {
    setSelectedGamesForDownload(prev => {
      const newSet = new Set(prev);
      if (newSet.has(codGame)) {
        newSet.delete(codGame);
      } else {
        newSet.add(codGame);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (data && data.items) {
      const gamesWithDocuments = data.items.filter(game => game.flg_documents);
      if (selectedGamesForDownload.size === gamesWithDocuments.length) {
        // Deselect all
        setSelectedGamesForDownload(new Set());
      } else {
        // Select all
        setSelectedGamesForDownload(new Set(gamesWithDocuments.map(game => game.cod_game)));
      }
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedGamesForDownload.size === 0) return;
    
    setIsDownloadingBatch(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const codGame of Array.from(selectedGamesForDownload)) {
      try {
        await handleDownload(codGame);
        successCount++;
        // Small delay between downloads to avoid overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        errorCount++;
      }
    }
    
    setIsDownloadingBatch(false);
    setSelectedGamesForDownload(new Set());
    
    if (successCount > 0) {
      push({ 
        title: `${successCount} refert${successCount > 1 ? 'i' : 'o'} scaricat${successCount > 1 ? 'i' : 'o'} con successo`, 
        type: 'success' 
      });
    }
    if (errorCount > 0) {
      push({ 
        title: `Errore nel download di ${errorCount} refert${errorCount > 1 ? 'i' : 'o'}`, 
        type: 'error' 
      });
    }
  };

  useEffect(() => {
    if (data) {
      setItemsList(getListItems(
        intl, 
        data.items, 
        "ref", 
        false, 
        false, 
        true, 
        undefined, 
        handleDownload, 
        handleUploadClick, 
        handleDeleteClick, 
        uploadingGame, 
        deletingGame, 
        currentUserId, 
        canEditAny,
        !isMember ? selectedGamesForDownload : undefined,
        !isMember ? handleToggleSelection : undefined,
        handleMarkAtOfficeClick,
        markingAtOfficeGame,
        handleReportAnomalyClick,
        reportingAnomalyGame,
        handleValidateDocumentClick,
        validatingDocumentGame
      ));
    }
  }, [data, uploadingGame, deletingGame, selectedGamesForDownload, markingAtOfficeGame, reportingAnomalyGame, validatingDocumentGame]);

  return (
    <>
      <UploadDocumentModal
        isOpen={uploadModalOpen}
        onClose={handleCloseModal}
        onUpload={handleUploadFiles}
        isReplacing={isReplacingDocument}
        isUploading={!!uploadingGame}
      />
      <DeleteDocumentModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        isDeleting={!!deletingGame}
      />
      <MarkAtOfficeModal
        isOpen={markAtOfficeModalOpen}
        onClose={handleCloseMarkAtOfficeModal}
        onConfirm={handleMarkAtOfficeConfirm}
        isMarking={!!markingAtOfficeGame}
      />
      <ReportAnomalyModal
        isOpen={reportAnomalyModalOpen}
        onClose={handleCloseReportAnomalyModal}
        onConfirm={handleReportAnomalyConfirm}
        isReporting={!!reportingAnomalyGame}
        gameInfo={data?.items?.find(g => g.cod_game === selectedGameForReportAnomaly) ? 
          `${data.items.find(g => g.cod_game === selectedGameForReportAnomaly)?.team_a?.team_alias || 'N/A'} vs ${data.items.find(g => g.cod_game === selectedGameForReportAnomaly)?.team_b?.team_alias || 'N/A'}` 
          : undefined
        }
      />
      <ValidateDocumentModal
        isOpen={validateDocumentModalOpen}
        onClose={handleCloseValidateDocumentModal}
        onConfirm={handleValidateDocumentConfirm}
        isValidating={!!validatingDocumentGame}
        gameInfo={data?.items?.find(g => g.cod_game === selectedGameForValidate) ? 
          `${data.items.find(g => g.cod_game === selectedGameForValidate)?.team_a?.team_alias || 'N/A'} vs ${data.items.find(g => g.cod_game === selectedGameForValidate)?.team_b?.team_alias || 'N/A'}` 
          : undefined
        }
        anomalyDescription={data?.items?.find(g => g.cod_game === selectedGameForValidate)?.des_anomaly}
      />
      <PageHeading
        title="Referti"
      />
      {levels ? (
        <>
          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <div className="w-full sm:w-1/3">
              <GamesPeriodFilter
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                setOffset={setOffset}
              />
            </div>
            <div className="w-full sm:w-1/3">
              <GamesLevelFilter
                levels={levels}
                selectedLevels={selectedLevels}
                setSelectedLevels={setSelectedLevels}
                setOffset={setOffset}
              />
            </div>
            {isMember ? (
              <div className="w-full sm:w-1/3">
                <MemberSearch
                  label="Cerca gara"
                  searchKey={filterGameNumber}
                  setSearchKey={setFilterGameNumber}
                  setOffset={setOffset}
                />
              </div>
            ) : (
              <div className="w-full sm:w-1/3">
                <GamesProvinceFilter
                  selectedProvinces={selectedProvinces}
                  setSelectedProvinces={setSelectedProvinces}
                  setOffset={setOffset}
                />
              </div>
            )}

          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-4 items-center">
            {!isMember && (
              <div className="w-full sm:w-1/4">
                <MemberSearch
                  label="Cerca gara"
                  searchKey={filterGameNumber}
                  setSearchKey={setFilterGameNumber}
                  setOffset={setOffset}
                />
              </div>
            )}

            {isMember ? (
              <div className="mb-3">
                <Toggle
                  smaller
                  vertical
                  description=""
                  label={<span className="mr-2">Includi gare da 2° arbitro</span>}
                  checked={includeSecondRef}
                  onChange={() => {
                    setIncludeSecondRef(!includeSecondRef);
                    setOffset(0);
                  }}
                />
              </div>
            ) : (
              <div className="w-full sm:w-1/4">
                <MemberSearch
                  label="Cerca arbitro"
                  searchKey={filterMember}
                  setSearchKey={setFilterMember}
                  setOffset={setOffset}
                />
              </div>
            )}

            <div className="flex flex-col gap-2 mb-3">
              <span className="text-sm font-medium text-gray-700">Mostra referti:</span>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showMissing}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      // Se deseleziono entrambi, riseleziono entrambi
                      if (!newValue && !showUploaded) {
                        setShowMissing(true);
                        setShowUploaded(true);
                      } else {
                        setShowMissing(newValue);
                      }
                      setOffset(0);
                    }}
                    className="h-4 w-4 text-am-600 border-gray-300 rounded focus:ring-am-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Mancanti</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showUploaded}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      // Se deseleziono entrambi, riseleziono entrambi
                      if (!newValue && !showMissing) {
                        setShowMissing(true);
                        setShowUploaded(true);
                      } else {
                        setShowUploaded(newValue);
                      }
                      setOffset(0);
                    }}
                    className="h-4 w-4 text-am-600 border-gray-300 rounded focus:ring-am-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Caricati</span>
                </label>
              </div>
            </div>

            {data && data.num_missing > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                <p className="text-sm font-semibold text-red-700">
                  ⚠️ Referti mancanti: <span className="text-lg">{data.num_missing}</span>
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-row gap-10">
          <Placeholder height="h-10" classNames="rounded-lg w-1/3" />
          <Placeholder height="h-10" classNames="rounded-lg w-1/3" />
          <Placeholder height="h-10" classNames="rounded-lg w-1/3" />
        </div>
      )}
      
      {!isMember && selectedGamesForDownload.size > 0 && (
        <div className="my-4 bg-am-50 border border-am-300 rounded-lg px-4 py-3 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-am-700">
              {selectedGamesForDownload.size} refert{selectedGamesForDownload.size > 1 ? 'i' : 'o'} selezionat{selectedGamesForDownload.size > 1 ? 'i' : 'o'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm font-medium text-am-700 bg-white border border-am-300 rounded-lg hover:bg-am-50 transition-colors"
            >
              {data && selectedGamesForDownload.size === data.items.filter(g => g.flg_documents).length ? 'Deseleziona tutto' : 'Seleziona tutto'}
            </button>
            <button
              onClick={handleDownloadSelected}
              disabled={isDownloadingBatch}
              className="px-4 py-2 text-sm font-medium text-white bg-am-600 rounded-lg hover:bg-am-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDownloadingBatch ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Download in corso...
                </>
              ) : (
                <>Scarica selezionati</>
              )}
            </button>
            <button
              onClick={() => setSelectedGamesForDownload(new Set())}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
      
      <div className="my-5">
        <RefManList
          offset={offset}
          setOffset={setOffset}
          limit={limit}
          total={data ? data.total : 0}
          containerId="observer-reports-list"
          items={itemsList}
          rowClassNames={["sm:grid", "sm:grid-cols-5", "sm:gap-3", "!cursor-default"]}
          pagination="backend"
        />
      </div>
    </>
  );
}
